package org.gymly.ui.admin

import androidx.compose.runtime.Composable
import androidx.compose.runtime.staticCompositionLocalOf
import org.gymly.model.AdminSettingsPermissionModule

/**
 * Permisos del usuario actual, provistos una vez en [AdminRouteContent].
 * Si el backend no envió permisos, se muestran todos: el servidor sigue aplicando las reglas (403 / aprobación).
 */
val LocalAdminPermissions = staticCompositionLocalOf<Set<AdminSettingsPermissionModule>> {
    AdminSettingsPermissionModule.entries.toSet()
}

/**
 * Permisos por módulo, espejo de las reglas de NavOptionAccessInterceptor del backend.
 * - [write]: crear o editar. Con "Usar" la acción pide aprobación; con "Aprobar" es directa.
 * - [delete]: borrar. Con "Borrar" pide aprobación; con "Aprobar" es directa. "Usar" no basta para borrar.
 */
enum class AdminModuleAccess(
    val write: Set<AdminSettingsPermissionModule>,
    val delete: Set<AdminSettingsPermissionModule>,
) {
    CLIENTS(
        write = setOf(AdminSettingsPermissionModule.CLIENT_OPERATE, AdminSettingsPermissionModule.CLIENT_APPROVE),
        delete = setOf(AdminSettingsPermissionModule.CLIENT_DELETE, AdminSettingsPermissionModule.CLIENT_APPROVE),
    ),
    WORKERS(
        write = setOf(AdminSettingsPermissionModule.WORKER_OPERATE, AdminSettingsPermissionModule.WORKER_APPROVE),
        delete = setOf(AdminSettingsPermissionModule.WORKER_DELETE, AdminSettingsPermissionModule.WORKER_APPROVE),
    ),
    MEMBERSHIP_PLANS(
        write = setOf(AdminSettingsPermissionModule.MEMBERSHIPS_OPERATE, AdminSettingsPermissionModule.MEMBERSHIPS_APPROVE),
        delete = setOf(AdminSettingsPermissionModule.MEMBERSHIPS_DELETE, AdminSettingsPermissionModule.MEMBERSHIPS_APPROVE),
    ),
    MEMBERSHIPS(
        write = setOf(AdminSettingsPermissionModule.MEMBERSHIPS_OPERATE, AdminSettingsPermissionModule.MEMBERSHIPS_APPROVE),
        delete = setOf(AdminSettingsPermissionModule.MEMBERSHIPS_DELETE, AdminSettingsPermissionModule.MEMBERSHIPS_APPROVE),
    ),
    MEMBERSHIP_ORDERS(
        // Crear y editar órdenes: permisos propios de Órdenes. Cobrar una orden exige Caja (módulo PAYMENTS).
        write = setOf(
            AdminSettingsPermissionModule.MEMBERSHIP_ORDER_OPERATE,
            AdminSettingsPermissionModule.MEMBERSHIP_ORDER_APPROVE,
        ),
        delete = setOf(
            AdminSettingsPermissionModule.MEMBERSHIP_ORDER_DELETE,
            AdminSettingsPermissionModule.MEMBERSHIP_ORDER_APPROVE,
        ),
    ),
    SERVICES(
        write = setOf(AdminSettingsPermissionModule.MEMBERSHIPS_OPERATE, AdminSettingsPermissionModule.MEMBERSHIPS_APPROVE),
        delete = setOf(
            AdminSettingsPermissionModule.MEMBERSHIPS_DELETE,
            AdminSettingsPermissionModule.SERVICE_DELETE,
            AdminSettingsPermissionModule.SERVICE_APPROVE,
        ),
    ),
    PRODUCTS(
        write = setOf(AdminSettingsPermissionModule.PRODUCT_OPERATE, AdminSettingsPermissionModule.PRODUCT_APPROVE),
        delete = setOf(AdminSettingsPermissionModule.PRODUCT_DELETE, AdminSettingsPermissionModule.PRODUCT_APPROVE),
    ),
    MARKETING(
        write = setOf(AdminSettingsPermissionModule.MARKETING_OPERATE, AdminSettingsPermissionModule.MARKETING_APPROVE),
        delete = setOf(AdminSettingsPermissionModule.MARKETING_DELETE, AdminSettingsPermissionModule.MARKETING_APPROVE),
    ),
    PAYMENTS(
        // Confirmar y editar cobros usan Usar o Aprobar de Caja; anular usa Borrar o Aprobar de Caja.
        write = setOf(AdminSettingsPermissionModule.CASH_OPERATE, AdminSettingsPermissionModule.CASH_APPROVE),
        delete = setOf(AdminSettingsPermissionModule.CASH_DELETE, AdminSettingsPermissionModule.CASH_APPROVE),
    ),
    SALES(
        // Cobrar y editar cobro usan Caja (Usar o Aprobar); borrar usa Borrar o Aprobar de ventas.
        write = setOf(AdminSettingsPermissionModule.CASH_OPERATE, AdminSettingsPermissionModule.CASH_APPROVE),
        delete = setOf(AdminSettingsPermissionModule.SALE_DELETE, AdminSettingsPermissionModule.SALE_APPROVE),
    ),
}

/** ¿Puede crear o editar en este módulo? Usar en la condición del botón. */
@Composable
fun canWriteModule(module: AdminModuleAccess): Boolean =
    LocalAdminPermissions.current.any { it in module.write }

/** ¿Puede borrar en este módulo? Usar en la condición del botón. */
@Composable
fun canDeleteModule(module: AdminModuleAccess): Boolean =
    LocalAdminPermissions.current.any { it in module.delete }

/**
 * Contador de cambios de datos: sube cuando una aprobación se resuelve. Las pantallas de gestión lo observan
 * para recargar sin que el usuario refresque la página.
 */
val LocalAdminDataRevision = staticCompositionLocalOf { 0 }

/** Marca los datos como cambiados para que las pantallas de gestión recarguen al instante. */
val LocalAdminBumpDataRevision = staticCompositionLocalOf<() -> Unit> { {} }

/**
 * Permisos que le faltan al usuario para procesar una alta masiva: crear clientes, asignar plan y cobrar.
 * Lista vacía = puede procesar.
 */
@Composable
fun bulkMissingPermissions(): List<String> = buildList {
    if (!canWriteModule(AdminModuleAccess.CLIENTS)) add("Clientes")
    if (!canWriteModule(AdminModuleAccess.MEMBERSHIP_PLANS)) add("Membresías")
    if (!canWriteModule(AdminModuleAccess.PAYMENTS)) add("Caja y cobros")
}
